/**
 * Records what people actually ask.
 *
 * This is the real output of Phase 1. The widget itself is useful, but the log
 * is what makes Phase 2 possible: you cannot train a retriever, or judge
 * whether it improved anything, without a set of genuine questions and the
 * answers they should have got. Guessing at that set produces a model tuned for
 * questions nobody asks.
 *
 * What is stored is the question, which entry matched (or none), and the score.
 * No name, no email, no cart, no user id — there is no reason for this
 * collection to hold anything that identifies a person, and every reason for it
 * not to.
 *
 * SECURITY: this writes to Firestore from an unauthenticated browser. That is
 * an open write endpoint unless the rules constrain it. See the rule in
 * firestore.rules.example — without it, anyone can write arbitrary documents to
 * your project. The client-side cap below deters casual abuse; it is not a
 * security control, because anyone can call Firestore directly.
 */
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../config/firebase";

const COLLECTION = "chatQuestions";
const SESSION_CAP = 25;
const CAP_KEY = "nt_chat_logged";
const MAX_LEN = 300;

function loggedThisSession() {
  try {
    return Number(sessionStorage.getItem(CAP_KEY) || 0);
  } catch {
    return 0; // Private mode or blocked storage — treat as first message.
  }
}

function bumpCount(n) {
  try {
    sessionStorage.setItem(CAP_KEY, String(n));
  } catch {
    /* Nothing to do — the cap is best-effort. */
  }
}

/**
 * Fire-and-forget. A failed log must never break the answer the visitor is
 * waiting on, so this resolves quietly on error rather than throwing.
 */
export async function logQuestion({ question, matchedId, score, path }) {
  const text = String(question || "").trim().slice(0, MAX_LEN);
  if (!text) return;

  const count = loggedThisSession();
  if (count >= SESSION_CAP) return;
  bumpCount(count + 1);

  try {
    await addDoc(collection(db, COLLECTION), {
      question: text,
      matchedId: matchedId || null,
      answered: Boolean(matchedId),
      score: Number(score.toFixed(3)),
      path: String(path || "").slice(0, 120),
      createdAt: serverTimestamp(),
    });
  } catch (err) {
    // Rules may reject this, or the visitor may be offline. Either way the
    // widget keeps working; we lose one row of training data.
    if (process.env.NODE_ENV === "development") {
      console.warn("chat question not logged", err);
    }
  }
}
